import * as types from './constants';
import uuidv4 from 'uuid/v4';
import arrayMove from 'array-move';

const defaultGeojson = {
  type: 'FeatureCollection',
  name: 'Untitled',
  features: []
};

// Colors based on those found in the default style.
const initialState = {
  intro: true,
  modifiedPlaceName: [],
  activeFeature: null,
  mapResult: {
    geocode: {
      text: 'Untitled',
      center: [0, 0]
    },
    zoom: 0
  },
  geojson: defaultGeojson
};

const reducer = (state = initialState, action) => {
  function setField(id, field, value) {
    return Object.assign({}, state.geojson, {
      features: state.geojson.features.map(feature => {
        const { __id: id } = feature.properties;
        if (id === action.id) {
          return Object.assign({}, feature, {
            properties: Object.assign({}, feature.properties, {
              [field]: value
            })
          });
        } else {
          return feature;
        }
      })
    });
  }

  function getModifiedList(list, id) {
    if (list.indexOf(id) === -1) {
      list.push(id);
    }

    return list;
  }

  switch (action.type) {
    case types.HOME: {
      return Object.assign({}, state, {
        intro: action.state
      });
    }

    case types.CREATE_NEW_GAZETTEER: {
      return Object.assign({}, initialState, {
        intro: false
      });
    }

    case types.CREATE_FROM_EXISTING: {
      const geojson = Object.assign({}, action.geojson, {
        features: action.geojson.features.map(feature => {
          return Object.assign({}, feature, {
            properties: {
              __id: uuidv4(),
              ...feature.properties
            }
          });
        })
      });

      return Object.assign({}, initialState, {
        activeFeature: geojson.features[0],
        modifiedPlaceName: geojson.features.map(
          feature => feature.properties.__id
        ),
        intro: false,
        geojson
      });
    }

    case types.FEATURE_ADD: {
      const { text, center } = state.mapResult.geocode;

      const feature = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: center
        },
        properties: {
          __id: uuidv4(),
          place_name: text,
          zoom: state.mapResult.zoom
        }
      };

      return Object.assign({}, state, {
        activeFeature: feature,
        geojson: Object.assign({}, state.geojson, {
          features: [feature, ...state.geojson.features]
        })
      });
    }

    case types.SET_POSITION: {
      const { text, center } = state.mapResult.geocode;
      const { zoom } = state.mapResult;

      return Object.assign({}, state, {
        geojson: Object.assign({}, state.geojson, {
          features: state.geojson.features.map(feature => {
            const { __id: id } = feature.properties;
            if (id === action.id) {
              const modifiedFeature = {
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: center
                }
              };

              // If placename has previously been modified, we dont want to
              // write over changes by updating `place_name`.
              if (state.modifiedPlaceName.indexOf(action.id) >= 0) {
                modifiedFeature.properties = Object.assign(
                  {},
                  feature.properties,
                  {
                    zoom
                  }
                );
              } else {
                modifiedFeature.properties = Object.assign(
                  {},
                  feature.properties,
                  {
                    place_name: text,
                    zoom
                  }
                );
              }

              return modifiedFeature;
            } else {
              return feature;
            }
          })
        })
      });
    }

    case types.FEATURE_ACTIVE: {
      return Object.assign({}, state, {
        activeFeature: action.feature
      });
    }

    case types.FEATURE_DELETE: {
      return Object.assign({}, state, {
        geojson: Object.assign({}, state.geojson, {
          features: state.geojson.features.filter(feature => {
            return feature.properties.__id !== action.id;
          })
        })
      });
    }

    case types.FEATURE_UPDATE_INDEX: {
      return Object.assign({}, state, {
        geojson: Object.assign({}, state.geojson, {
          features: arrayMove(
            state.geojson.features,
            action.oldIndex,
            action.newIndex
          )
        })
      });
    }

    case types.SET_NAME: {
      return Object.assign({}, state, {
        geojson: Object.assign({}, state.geojson, {
          name: action.name
        })
      });
    }

    case types.SET_PLACE_NAME: {
      // `place_name` has been modified so push an entry if one doesnt exit
      // to the modifiedPlaceName bucket
      return Object.assign({}, state, {
        geojson: setField(action.id, 'place_name', action.placeName),
        modifiedPlaceName: getModifiedList(state.modifiedPlaceName, action.id)
      });
    }

    case types.SET_PLACE_DESCRIPTION: {
      return Object.assign({}, state, {
        geojson: setField(
          action.id,
          'place_description',
          action.placeDescription
        )
      });
    }

    case types.SET_TAGS: {
      return Object.assign({}, state, {
        geojson: setField(action.id, 'tags', action.tags)
      });
    }

    case types.SET_META: {
      return Object.assign({}, state, {
        geojson: Object.assign({}, state.geojson, {
          features: state.geojson.features.map(feature => {
            const { __id: id } = feature.properties;
            if (id === action.id) {
              return Object.assign({}, feature, {
                properties: Object.assign({}, feature.properties, action.meta)
              });
            } else {
              return feature;
            }
          })
        })
      });
    }

    case types.SET_MAP_RESULT: {
      return Object.assign({}, state, {
        mapResult: {
          geocode: action.result,
          zoom: action.zoom
        }
      });
    }

    default:
      return state;
  }
};

export { reducer, initialState };
